import { useCallback, useEffect, useMemo, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useDispatch, useSelector } from "react-redux";

import {
  INFERENCE_STATUSES,
  InferenceStatus,
  LayerAlias,
  UnitTypes,
  UserPolices
} from "constant";
import features from "features";
import { spendTime } from "helpers/spendTime";
import usePolicy from "hooks/usePolicy";
import { RootStateInterface } from "reducer";
import { LayersManagerStateInterface } from "scenes/LayersManager/interfaces";

import CommonButton from "components/buttons/CommonButton/CommonButton";
import IconButton from "components/buttons/IconButton/IconButton";
import PrimaryButton from "components/buttons/PrimaryButton/PrimaryButton";
import ShowHideButton from "components/buttons/ShowHideButton/ShowHideButton";
import InferenceAction from "components/InferenceAction/InferenceAction";
import InferenceStatusComponent from "components/InferenceStatusComponent/InferenceStatusComponent";
import ModalWrapper from "components/modals/ModalWrapper/ModalWrapper";

import DragHandleDotsIcon from "assets/icons/drag-handle-dots.svg";
import TrashIcon from "assets/icons/trash.svg";
// import EyeCloseGrayIcon from "assets/icons/eye-close-gray.svg";
// import EyeOpenGrayIcon from "assets/icons/eye-open-gray.svg";
import ArrowLeftIcon from "assets/icons/arrow-left-small.svg";
import FolderIcon from "assets/icons/folder-closed.png";
import MarkersFlagOffIcon from "assets/icons/markers-flag-off.svg";
import MarkersFlagOnIcon from "assets/icons/markers-flag.svg";
import PlusIcon from "assets/icons/plus-black.svg";

import ProviderFolder from "./components/ProviderFolder/ProviderFolder";

import styles from "./provider-folders-files-manager-modal.module.scss";

export interface ProviderFoldersFilesManagerModalInterface {
  title?: string;
  description?: string;
  isDisableClose?: boolean;
  acceptButtonLabel?: string;
  acceptButtonAction?: () => void;
  isWithProgress?: boolean;
  id: string;
  alias: LayerAlias;
  initProviders: any;
  onHide: (updatedProviders: any) => void;
}

const ProviderFoldersFilesManagerModal = () => {
  const [isFolderView, setIsFolderView] = useState(false);
  const [folderId, setFolderId] = useState("");

  const dispatch = useDispatch();
  const { hasPolicy } = usePolicy();

  const {
    title,
    description,
    isDisableClose,
    alias,
    id,
    initProviders,
    onHide
  } = useSelector<
    RootStateInterface,
    ProviderFoldersFilesManagerModalInterface
  >((state) => state.modal.modalProps);

  const { providers } = useSelector<
    RootStateInterface,
    LayersManagerStateInterface
  >((state) => state.layersManager);

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const { destination, draggableId: providerId } = result;
    const index = destination.index;

    dispatch(
      features.layersManager.actions.reorderProviderRequest({
        params: { id, alias, providerId },
        fields: { index }
      })
    );
  };

  const onDelete = useCallback(
    (providerId, isOnlyGeocore) => () => {
      dispatch(
        features.layersManager.actions.deleteProviderRequest({
          params: { id, alias, providerId },
          fields: { isOnlyGeocore }
        })
      );
    },
    [alias, dispatch, id]
  );

  useEffect(() => {
    dispatch(
      features.layersManager.actions.setProviders({
        params: { alias, id },
        providers: initProviders
      })
    );
  }, [alias, dispatch, id, initProviders]);

  useEffect(() => {
    return () => {
      dispatch(features.layersManager.actions.clearProvidersState(alias));
    };
  }, [alias, dispatch]);

  const inferenceDurationTime = useCallback((inference) => {
    if (!inference) return "";

    const { status, data } = inference;

    if (
      status === InferenceStatus.PROCESSING ||
      status === InferenceStatus.DONE
    ) {
      return spendTime({
        startTime: data.stats?.inference_time_start_at,
        endTime: data.stats?.inference_time_end_at
      });
    }
  }, []);
  const uploadDurationTime = useCallback((inference) => {
    if (!inference) return "";

    const { data } = inference;

    if (data)
      return spendTime({
        startTime: data.stats?.upload_time_start_at,
        endTime: data.stats?.upload_time_end_at
      });
  }, []);

  const onUpdateVisibilityProvidersMarkers = useCallback(
    (provider) => () => {
      dispatch(
        features.unitsManager.actions.clearUnits({
          params: {
            type: UnitTypes.POINT,
            alias
          }
        })
      );
      dispatch(
        features.layersManager.actions.setProvidersMarkersIsActive({
          providerId: provider.id,
          isActiveMarkers: !provider?.isActiveMarkers,
          alias
        })
      );
    },
    [dispatch, alias]
  );

  const isAtLeastOneVisibleProvidersMarkers = useMemo(
    () => providers.some((item) => item.isActiveMarkers),
    [providers]
  );

  const onUpdateVisibilityAllProvidersMarkers = useCallback(() => {
    dispatch(
      features.unitsManager.actions.clearUnits({
        params: {
          type: UnitTypes.POINT,
          alias
        }
      })
    );
    dispatch(
      features.layersManager.actions.setAllProvidersMarkersIsActive({
        isActiveMarkers: !isAtLeastOneVisibleProvidersMarkers,
        alias
      })
    );
  }, [dispatch, alias, isAtLeastOneVisibleProvidersMarkers]);

  const onOpenFolder = useCallback(
    (providerId) => () => {
      setFolderId(providerId);
      setIsFolderView(true);
      dispatch(
        features.layersManager.actions.fetchProvidersPhotosRequest({
          params: {
            projectId: id,
            alias,
            provider: providerId
          }
        })
      );
    },
    [alias, dispatch, id]
  );
  const onCreateFolder = useCallback(() => {
    setIsFolderView(true);
  }, []);
  const onCloseFolder = useCallback(() => {
    setIsFolderView(false);
    setFolderId("");
  }, []);

  const onPrepareProvider = useCallback(
    (providerId) => () => {
      dispatch(
        features.layersManager.actions.prepareProviderInferenceRequest({
          params: { id, alias, providerId }
        })
      );
    },
    [alias, dispatch, id]
  );

  const onClose = useCallback(() => {
    onHide(providers);
    dispatch(features.modal.actions.hideModal());
    dispatch(
      features.layersManager.actions.setLayerIsActive({
        params: { alias },
        fields: { isActive: isAtLeastOneVisibleProvidersMarkers }
      })
    );
    dispatch(features.layersManager.actions.clearProvidersState(alias));
  }, [dispatch, onHide, providers, alias, isAtLeastOneVisibleProvidersMarkers]);

  if (isFolderView) {
    return (
      <ModalWrapper
        className={styles["modal-container-folder"]}
        isDisableClose={isDisableClose}
        onHide={onClose}
      >
        <div className={styles["modal"]}>
          <div className={styles["modal__text-container"]}>
            <h2 className={styles["modal__title"]}>
              <div onClick={onCloseFolder} className={styles["modal__back"]}>
                <img src={ArrowLeftIcon} alt="arrow-back" />
              </div>{" "}
              {title}
            </h2>
            <h4 className={styles["modal__description"]}>Folder files</h4>
            <ProviderFolder
              id={id}
              alias={alias}
              folderId={folderId}
              setFolderId={setFolderId}
            />
          </div>
          <div className={styles["modal__controls-wrapper"]}>
            <PrimaryButton type="button" onClick={onClose} textSize={14}>
              Done
            </PrimaryButton>
          </div>
        </div>
      </ModalWrapper>
    );
  }

  return (
    <ModalWrapper
      className={styles["modal-container"]}
      isDisableClose={isDisableClose}
      onHide={onClose}
    >
      <div className={styles["modal"]}>
        <div className={styles["modal__text-container"]}>
          <h2 className={styles["modal__title"]}>{title}</h2>
          <h4 className={styles["modal__description"]}>
            {description ?? "Manage files"}
          </h4>
          <div className={styles["modal__providers-table"]}>
            <div className={styles["providers-table__head"]}>
              <div className={styles["providers-table__head-controls"]}>
                {/* <ShowHideButton
                  onClick={onUpdateVisibilityAllProvidersMarkers}
                  isActive={isAtLeastOneVisibleProvider}
                  enabledIcon={EyeOpenGrayIcon}
                  disabledIcon={EyeCloseGrayIcon}
                /> */}
                <ShowHideButton
                  onClick={onUpdateVisibilityAllProvidersMarkers}
                  isActive={isAtLeastOneVisibleProvidersMarkers}
                  enabledIcon={MarkersFlagOnIcon}
                  disabledIcon={MarkersFlagOffIcon}
                />
              </div>
              <div>Name</div>
              <div>Action</div>
              <div>Time</div>
              <div>Inference action</div>
              <div>Time</div>
              <div>Inference status</div>
            </div>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="droppable">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {providers?.map((item, index) => (
                      <Draggable
                        key={item.id}
                        draggableId={item.id.toString()}
                        index={index}
                        isDragDisabled={
                          !hasPolicy(UserPolices.PROJECT_PROVIDER_REORDER)
                        }
                      >
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={{
                              ...provided.draggableProps.style
                            }}
                            className={styles["providers-table__body"]}
                          >
                            <div className={styles["drag-dots"]}>
                              <img src={DragHandleDotsIcon} alt="drag-dots" />
                              {/* <ShowHideButton
                                onClick={onUpdateVisibilityProvidersMarkers(
                                  item
                                )}
                                isActive={item.isActiveMarkers}
                                enabledIcon={EyeOpenGrayIcon}
                                disabledIcon={EyeCloseGrayIcon}
                              /> */}
                              <ShowHideButton
                                onClick={onUpdateVisibilityProvidersMarkers(
                                  item
                                )}
                                isActive={item.isActiveMarkers}
                                enabledIcon={MarkersFlagOnIcon}
                                disabledIcon={MarkersFlagOffIcon}
                              />
                            </div>
                            <div className={styles["name"]}>
                              {item.data.name}
                            </div>
                            <div className={styles["actions"]}>
                              <CommonButton
                                isSmall
                                icon={FolderIcon}
                                onClick={onOpenFolder(item.id)}
                                disabled={
                                  item?.inference?.status ===
                                  InferenceStatus.UPLOADING
                                }
                              >
                                Open folder
                              </CommonButton>
                              <IconButton
                                onClick={onDelete(
                                  item.id,
                                  !item?.inference?.status
                                )}
                                icon={TrashIcon}
                                alt="trash-icon"
                                disabled={item.isDeleteProviderLoading}
                                isLoading={item.isDeleteProviderLoading}
                              />
                            </div>
                            <div className={styles["name"]}>
                              {uploadDurationTime(item.inference)}
                            </div>

                            {item?.inference?.status ? (
                              <InferenceAction
                                id={id}
                                providerId={item.id}
                                alias={alias}
                                status={
                                  INFERENCE_STATUSES[item?.inference?.status]
                                }
                                isLoading={item.isInferenceStatusWaiting}
                                disabled={item.isDeleteProviderLoading}
                              />
                            ) : (
                              <div className={styles["name"]}>
                                <CommonButton
                                  onClick={onPrepareProvider(item.id)}
                                >
                                  Prepare to inference
                                </CommonButton>
                              </div>
                            )}
                            <div className={styles["name"]}>
                              {inferenceDurationTime(item.inference)}
                            </div>
                            <InferenceStatusComponent
                              status={
                                INFERENCE_STATUSES[item?.inference?.status]
                              }
                              completionPercentage={
                                item?.inference?.status ===
                                InferenceStatus.PROCESSING
                                  ? item?.inference?.data?.completion_percentage
                                  : item?.inference?.fileUploadData
                                  ? item?.inference?.fileUploadData.progress
                                  : undefined
                              }
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
            {hasPolicy(UserPolices.PROJECT_PROVIDER_CREATE) && (
              <div className={styles["modal__upload"]}>
                <label className={styles["label"]}>
                  Please create a folder
                </label>
                <CommonButton isSmall icon={PlusIcon} onClick={onCreateFolder}>
                  Create folder
                </CommonButton>
                {/* <label className={styles["label"]}>
                  Please select a file to upload
                </label> */}
                {/* <UploadFiles title="Upload zip" handleFileChange={onSubmit} /> */}
              </div>
            )}
          </div>
        </div>
        <div className={styles["modal__controls-wrapper"]}>
          <PrimaryButton type="button" onClick={onClose} textSize={14}>
            Done
          </PrimaryButton>
        </div>
      </div>
    </ModalWrapper>
  );
};
export default ProviderFoldersFilesManagerModal;
